import React, { useState, useEffect, useRef } from 'react';
import { Minus, Plus, Clock } from 'lucide-react';
import { type GameProvider } from '@/features/games/data/gameProviders';
import { playClickSound } from '@/utils/soundUtils';
import { getDisplayIntegerDollars } from '@/utils/amountStep';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import QuickAmountModal from './QuickAmountModal';
import './GameProviderCard.css';

export interface GameProviderCardProps {
  provider: GameProvider;
  amount: number;
  onAmountChange: (providerId: string, amount: number) => void;
  onIncrement: (providerId: string) => void;
  onDecrement: (providerId: string) => void;
  gameBalance?: number;
  /** River Pay: withdrawable sweepstakes balance (info only) */
  winningBalance?: number;
  maxAmount?: number;
  /** When true, +/- buttons use maxAmount as limit (e.g. for Deposit). When false, use gameBalance if showBalance. */
  useMaxAmountForControls?: boolean;
  showBalance?: boolean;
  statusText?: string;
  onSelect?: (provider: GameProvider) => void;
  providerStatus?: 'active' | 'inactive' | 'maintenance';
}

const GameProviderCard: React.FC<GameProviderCardProps> = ({
  provider,
  amount,
  onAmountChange,
  onIncrement,
  onDecrement,
  gameBalance = 0,
  winningBalance,
  maxAmount = 10000,
  useMaxAmountForControls = false,
  showBalance = false,
  statusText,
  onSelect,
  providerStatus
}) => {
  const [incrementInterval, setIncrementInterval] = useState<NodeJS.Timeout | null>(null);
  const [decrementInterval, setDecrementInterval] = useState<NodeJS.Timeout | null>(null);
  const [quickAmountModalOpen, setQuickAmountModalOpen] = useState(false);
  const touchHandledRef = useRef(false);
  const isAdded = amount > 0;

  useEffect(() => {
    return () => {
      if (incrementInterval) clearInterval(incrementInterval);
      if (decrementInterval) clearInterval(decrementInterval);
    };
  }, [incrementInterval, decrementInterval]);

  const limit = useMaxAmountForControls ? maxAmount : (showBalance ? gameBalance : maxAmount);

  const handleIncrementMouseDown = (e: React.MouseEvent) => {
    if (touchHandledRef.current) {
      touchHandledRef.current = false;
      return;
    }
    e.stopPropagation();
    if (amount < limit) {
      playClickSound();
      onIncrement(provider.id);
      const interval = setInterval(() => {
        onIncrement(provider.id);
      }, 100);
      setIncrementInterval(interval);
    }
  };

  const handleIncrementMouseUp = () => {
    if (incrementInterval) {
      clearInterval(incrementInterval);
      setIncrementInterval(null);
    }
  };

  const handleIncrementTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
    touchHandledRef.current = true;
    if (amount < limit) {
      playClickSound();
      onIncrement(provider.id);
      const interval = setInterval(() => {
        onIncrement(provider.id);
      }, 100);
      setIncrementInterval(interval);
    }
    setTimeout(() => {
      touchHandledRef.current = false;
    }, 300);
  };

  const handleIncrementTouchEnd = () => {
    handleIncrementMouseUp();
  };

  const handleDecrementMouseDown = (e: React.MouseEvent) => {
    if (touchHandledRef.current) {
      touchHandledRef.current = false;
      return;
    }
    e.stopPropagation();
    if (amount > 0) {
      playClickSound();
      onDecrement(provider.id);
      const interval = setInterval(() => {
        onDecrement(provider.id);
      }, 100);
      setDecrementInterval(interval);
    }
  };

  const handleDecrementMouseUp = () => {
    if (decrementInterval) {
      clearInterval(decrementInterval);
      setDecrementInterval(null);
    }
  };

  const handleDecrementTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
    touchHandledRef.current = true;
    if (amount > 0) {
      playClickSound();
      onDecrement(provider.id);
      const interval = setInterval(() => {
        onDecrement(provider.id);
      }, 100);
      setDecrementInterval(interval);
    }
    setTimeout(() => {
      touchHandledRef.current = false;
    }, 300);
  };

  const handleDecrementTouchEnd = () => {
    handleDecrementMouseUp();
  };

  const isComingSoon = provider.status === 'inactive';
  const displayBalance = showBalance ? gameBalance : undefined;
  // When logged in (showBalance): show balance with Figma styles; hide statusText (e.g. ACTIVE SESSION).
  const showBalanceLine = !isComingSoon && showBalance && displayBalance !== undefined;
  const displayStatus = showBalanceLine ? undefined : statusText;
  const isRiverPay =
    (provider.code || '')
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/-/g, '') === 'riverpay';
  const showWinningLine =
    showBalanceLine && isRiverPay && winningBalance !== undefined && winningBalance !== null;

  /** Two money rows (Balance + redeemable balance) → slightly smaller type than a single Balance line; title matches money lines. */
  const compactBalanceBlock = showWinningLine;
  const titleClassWhenBalanceShown = compactBalanceBlock
    ? 'text-white text-[8px] sm:text-[9px] md:text-[11px] lg:text-[11px] font-black leading-tight truncate min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-left'
    : 'text-white text-[8px] sm:text-[10px] md:text-[13px] lg:text-[13px] font-black leading-tight truncate min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-left';
  const moneyLineClassName = compactBalanceBlock
    ? 'game-card-balance-line text-[8px] sm:text-[9px] md:text-[11px] lg:text-[11px] leading-[1.2] text-left sm:text-left ipad:text-center w-full m-0'
    : 'game-card-balance-line text-[8px] sm:text-[10px] md:text-[13px] lg:text-[13px] leading-[1.25] text-left sm:text-left ipad:text-center w-full m-0';

  // Card state: selected (amount > 0) or unselected (amount === 0)
  const isSelected = amount > 0;
  const clientTagText =
    provider.clientTag != null && String(provider.clientTag).trim() !== ''
      ? String(provider.clientTag).trim()
      : '';

  // Unified card component - single card with two states (selected/unselected)
  // Scoped class names (game-card-mobile-{id}) so one card's selected state doesn't affect others
  return (
    <>
      <div
        className="relative flex items-center w-full max-w-full min-w-0 h-[92px] sm:h-[100px] lg:h-[96px] game-card-mobile overflow-visible"
        data-selected={isSelected ? 'true' : 'false'}
        style={{ isolation: 'isolate' }}
      >
        {clientTagText !== '' && (
          <div
            className="game-card-client-tag absolute z-10 pointer-events-none right-0.5 top-1.5"
            title={clientTagText}
          >
            <span className="game-card-client-tag__label">{clientTagText}</span>
            <span className="game-card-client-tag__flame" aria-hidden="true">
              <DotLottieReact
                src="/lottie/fire.lottie"
                autoplay
                loop
                className="game-card-client-tag__flame-player"
              />
            </span>
          </div>
        )}

        {/* ADDED Tag - top-right: half height outside (above), full width inside card (Figma: orange #FB923C) */}
        {isSelected && (
          <div
            className="absolute top-0 right-0 z-20 flex flex-col items-end flex-shrink-0 whitespace-nowrap sm:px-3 sm:py-1 sm:rounded-full"
            style={{
              transform: 'translateY(-50%)',
              padding: '2.372px 7.117px',
              borderRadius: '5930.441px',
              background: '#FB923C',
              boxShadow: '0 5.931px 8.897px -1.779px rgba(0, 0, 0, 0.10), 0 2.372px 3.559px -2.372px rgba(0, 0, 0, 0.10)',
            }}
          >
            <span className="text-white text-[8px] sm:text-[9px] font-black uppercase leading-[11px] sm:leading-[13px]">ADDED</span>
          </div>
        )}

        {/* Content Container */}
        <div className="flex items-center gap-2 sm:gap-3 lg:gap-2 flex-1 min-w-0 min-h-0 overflow-hidden">
          {/* Game Thumbnail - compact on desktop for 12 cards */}
          <div
            className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 lg:w-12 lg:h-12 rounded-lg border border-white/20 flex items-center justify-center overflow-hidden"
            style={{
              background: isSelected ? 'transparent' : 'linear-gradient(135deg, #0891B2 0%, #1E40AF 100%)',
              boxShadow: 'inset 0px 2px 4px 1px rgba(0, 0, 0, 0.05)'
            }}
          >
            <img
              src={provider.icon}
              alt={provider.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/icons/egame.jpg';
              }}
            />
          </div>

          {/* Game Info and Controls — extra gap before +/- so balance/winning aren’t glued to controls */}
          <div className="flex flex-col justify-between gap-1.5 sm:gap-2 lg:gap-1.5 flex-1 min-h-0 min-w-0 overflow-hidden">
            {/* Game Title and Status — uniform vertical rhythm between title / Balance / redeemable balance */}
            <div className="flex flex-col gap-1 sm:gap-1 min-w-0 overflow-hidden w-full">
              <div className="flex items-center gap-1 min-w-0 w-full max-w-full overflow-hidden">
                <h3
                  className={
                    showBalanceLine
                      ? titleClassWhenBalanceShown
                      : 'text-white text-[10px] sm:text-[14px] lg:text-[12px] font-black leading-tight truncate min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-left'
                  }
                >
                  {provider.name}
                </h3>
                {provider.depositType === 'manual' && (
                  <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-1 py-[1px] rounded-full bg-amber-500/25 border border-amber-400/30" title="Manual processing (5min – 2hrs)">
                    <Clock className="w-[5px] h-[5px] sm:w-[6px] sm:h-[6px] text-amber-400" />
                  </span>
                )}
              </div>
              {/* Balance + redeemable balance: same type scale, label 400 / value 900, even spacing via parent gap-1 */}
              {showBalanceLine && (
                <p
                  className={moneyLineClassName}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontStyle: 'normal',
                  }}
                >
                  <span style={{ color: '#FFF', fontWeight: 400 }}>Balance: </span>
                  <span style={{ color: '#EAB308', fontWeight: 900 }}>${getDisplayIntegerDollars(displayBalance)}</span>
                </p>
              )}
              {showWinningLine && (
                <p
                  className={moneyLineClassName}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontStyle: 'normal',
                  }}
                  title="Redeemable balance (e.g. River Pay)"
                >
                  <span style={{ color: '#FFF', fontWeight: 400 }}>Redeemable balance: </span>
                  <span style={{ color: '#86efac', fontWeight: 900 }}>${getDisplayIntegerDollars(winningBalance)}</span>
                </p>
              )}
              {!showBalanceLine && displayStatus && (
                <p
                  className="text-[8px] sm:text-[8px] lg:text-[7px] font-black leading-tight uppercase text-left w-full"
                  style={{ color: isComingSoon ? '#F59E0B' : '#22D3EE' }}
                >
                  {displayStatus}
                </p>
              )}
            </div>

            {/* Amount Controls - hidden for coming soon games */}
            {isComingSoon ? (
              <div
                className="flex flex-nowrap items-center justify-center gap-0.5 sm:gap-1 py-0.5 amount-controls-mobile min-h-[32px] sm:min-h-[28px] flex-shrink-0 w-full max-w-full overflow-hidden opacity-30 pointer-events-none"
                style={{
                  background: 'rgba(0, 0, 0, 0.4)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  height: '32px',
                }}
              >
                <span className="text-white/50 text-[9px] sm:text-[9px] font-semibold">COMING SOON</span>
              </div>
            ) : (
            /* Amount Controls */
            <div
              className="flex flex-nowrap items-center justify-center gap-0.5 sm:gap-1 py-0.5 amount-controls-mobile min-h-[32px] sm:min-h-[28px] flex-shrink-0 w-full max-w-full overflow-hidden"
              style={{
                background: 'rgba(0, 0, 0, 0.4)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                height: '32px',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Minus Button - same padding/margin as Plus, shifted slightly left */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onMouseDown={handleDecrementMouseDown}
                onMouseUp={handleDecrementMouseUp}
                onMouseLeave={handleDecrementMouseUp}
                onTouchStart={handleDecrementTouchStart}
                onTouchEnd={handleDecrementTouchEnd}
                disabled={amount === 0}
                className="flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 lg:w-6 lg:h-6 min-w-[24px] min-h-[24px] p-1 sm:p-1.5 rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10 active:scale-95 flex-shrink-0 touch-manipulation box-border"
                style={{ touchAction: 'manipulation' }}
                aria-label="Decrease amount"
              >
                <Minus className="w-2.5 h-3 sm:w-3.5 sm:h-4 text-white flex-shrink-0" />
              </button>

              {/* Amount Display - click to open quick amount modal */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  playClickSound();
                  setQuickAmountModalOpen(true);
                }}
                className="flex items-center justify-center h-6 sm:h-7 lg:h-6 flex-shrink overflow-hidden max-w-[72px] sm:max-w-[90px] lg:max-w-[72px] min-w-0 rounded cursor-pointer hover:bg-white/10 active:scale-95 transition-all"
              >
                <span
                  className="text-white font-extrabold amount-text-mobile text-center block truncate w-full"
                  style={{
                    fontFamily: '"JetBrains Mono"',
                    fontWeight: 800,
                    color: '#FFF',
                  }}
                >
                  ${Math.round(amount)}
                </span>
              </button>

              {/* Plus Button - touch-action to avoid zoom/scroll on mobile */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onMouseDown={handleIncrementMouseDown}
                onMouseUp={handleIncrementMouseUp}
                onMouseLeave={handleIncrementMouseUp}
                onTouchStart={handleIncrementTouchStart}
                onTouchEnd={handleIncrementTouchEnd}
                disabled={amount >= limit}
                className="flex items-center justify-center rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10 active:scale-95 flex-shrink-0 touch-manipulation w-6 h-6 sm:w-8 sm:h-8 min-w-[24px] min-h-[24px] sm:min-w-[32px] sm:min-h-[32px]"
                style={{ touchAction: 'manipulation' }}
                aria-label="Increase amount"
              >
                <Plus className="text-white w-2.5 h-3 sm:w-3 sm:h-3.5 lg:w-2.5 lg:h-3" />
              </button>
            </div>
            )}
          </div>
        </div>
      </div>

      <QuickAmountModal
        isOpen={quickAmountModalOpen}
        onClose={() => setQuickAmountModalOpen(false)}
        initialAmount={amount}
        onConfirm={(newAmount) => onAmountChange(provider.id, newAmount)}
        maxAmount={maxAmount}
        title={provider.name}
      />
    </>
  );
};

// Memoize to prevent re-renders when parent state changes but card props haven't
export default React.memo(GameProviderCard, (prev, next) => {
  return (
    prev.provider.id === next.provider.id &&
    prev.provider.clientTag === next.provider.clientTag &&
    prev.provider.depositType === next.provider.depositType &&
    prev.provider.status === next.provider.status &&
    prev.amount === next.amount &&
    prev.gameBalance === next.gameBalance &&
    prev.winningBalance === next.winningBalance &&
    prev.maxAmount === next.maxAmount &&
    prev.showBalance === next.showBalance &&
    prev.statusText === next.statusText &&
    prev.providerStatus === next.providerStatus
  );
});

